import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Package, FolderOpen, Send, MessageCircle, ShoppingCart, Users, Bot,
  Archive, CreditCard, Truck, BarChart3, Bell, Settings, Sparkles, LogOut, ShieldCheck, Headphones, X, Brain,
  LayoutDashboard, Store, UserPlus, ClipboardList, HeartPulse, AlertTriangle, Flame, Clock,
  Handshake, ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../lib/auth';
import api from '../../lib/api';
import BrandLogo from '../brand/BrandLogo';

// Feb 2026 — Stock & Inventory is HIDDEN from the seller-facing SaaS UI.
// The page, APIs, DB collections and product stock fields all remain
// intact so the feature can be restored later by flipping this single
// flag to `true`. Sidebar item + route access are both gated by it.
export const SHOW_STOCK_INVENTORY_UI = false;

const items = [
  { to: '/dashboard', label: 'Dashboard', icon: Home, end: true },
  { to: '/dashboard/products', label: 'Products', icon: Package },
  { to: '/dashboard/categories', label: 'Categories', icon: FolderOpen },
  { to: '/dashboard/broadcast', label: 'Catalog Broadcast', icon: Send },
  { to: '/dashboard/whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { to: '/dashboard/orders', label: 'Orders', icon: ShoppingCart },
  { to: '/dashboard/customers', label: 'Customers', icon: Users },
  { to: '/dashboard/support', label: 'Customer Support', icon: Headphones, badgeKey: 'support' },
  { to: '/dashboard/ai', label: 'AI Assistant', icon: Bot },
  { to: '/dashboard/sales-brain', label: 'Sales Brain', icon: Brain },
  ...(SHOW_STOCK_INVENTORY_UI
    ? [{ to: '/dashboard/inventory', label: 'Stock & Inventory', icon: Archive }]
    : []),
  { to: '/dashboard/payments', label: 'Payments', icon: CreditCard },
  { to: '/dashboard/shipping', label: 'Shipping', icon: Truck },
  { to: '/dashboard/analytics', label: 'Reports & Analytics', icon: BarChart3 },
  { to: '/dashboard/notifications', label: 'Notifications', icon: Bell },
  { to: '/dashboard/settings', label: 'Settings', icon: Settings },
];

const adminItems = [
  { to: '/dashboard/admin', label: 'Super Admin', icon: ShieldCheck, end: true },
  { to: '/dashboard/admin/vendors', label: 'Vendors', icon: ShieldCheck },
  { to: '/dashboard/admin/activity', label: 'Activity Logs', icon: ShieldCheck },
  { to: '/dashboard/admin/numbers', label: 'Pending Numbers', icon: ShieldCheck },
];

// The global Platform Admin sidebar — exactly 7 sections, in order. Rendered
// (instead of the seller nav) whenever the admin is inside /dashboard/admin.
// Seller navigation is intentionally NOT mixed in here.
const platformAdminNav = [
  { to: '/dashboard/admin',               label: 'Dashboard',            icon: LayoutDashboard, end: true },
  { to: '/dashboard/admin/sellers',       label: 'Sellers',              icon: Store },
  { to: '/dashboard/admin/buyers',        label: 'Buyers',               icon: Users },
  { to: '/dashboard/admin/guest-sellers', label: 'Guest Sellers',        icon: UserPlus },
  { to: '/dashboard/admin/activity',      label: 'Audit Logs',           icon: ClipboardList },
  { to: '/dashboard/admin/health',        label: 'Platform Health',      icon: HeartPulse },
  { to: '/dashboard/admin/errors',        label: 'Error / Issue Centre', icon: AlertTriangle },
  { to: '/dashboard/admin/master-sales-brain', label: 'Master Sales Brain', icon: Brain },
  { to: '/dashboard/admin/matrix',        label: 'Matrix',               icon: BarChart3 },
];

export default function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { seller, teamMember, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const inAdmin = pathname.startsWith('/dashboard/admin');
  // Sales Brain is an admin-only intelligence surface. Regular sellers never
  // see it; admins do, and so do admin impersonation tabs (which carry the
  // per-tab impersonation token) so per-seller analysis stays reachable.
  const isImpersonation = typeof window !== 'undefined' && !!sessionStorage.getItem('fab_imp_token');
  const canSeeSalesBrain = !!seller?.is_admin || isImpersonation;
  const [supportCount, setSupportCount] = useState(0);
  const [affiliateOpen, setAffiliateOpen] = useState(pathname.startsWith('/dashboard/admin/affiliate'));
  // Feb 2026 — when logged in as a team member, hide sidebar items outside
  // their assigned departments. The parent seller (admin) sees the full nav.
  // We read the department list from the shared AuthCtx (no extra fetch).
  const isTeamMember = !!teamMember;
  const allowedDepartments = isTeamMember
    ? new Set(teamMember.departments || [])
    : null;

  useEffect(() => {
    const load = () => api.get('/support/count').then(({ data }) => setSupportCount(data.open || 0)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    const onUpdate = () => load();
    window.addEventListener('support:refresh', onUpdate);
    return () => { clearInterval(t); window.removeEventListener('support:refresh', onUpdate); };
  }, []);

  const badgeFor = (key) => {
    if (key === 'support' && supportCount > 0) return supportCount;
    return null;
  };

  // Prevent body scroll when the mobile drawer is open so background
  // content doesn't jump around under the overlay.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (mobileOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      {/* Backdrop — mobile only */}
      {mobileOpen && (
        <div
          onClick={onClose}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-50 w-[260px] bg-white border-r border-fab-primary-lighter flex flex-col
          transform transition-transform duration-200 ease-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:sticky lg:top-0 lg:h-screen lg:w-[240px] lg:translate-x-0 lg:transition-none`}
      >
        <div className="px-5 py-5 border-b border-fab-primary-lighter flex items-center justify-between">
          <button onClick={() => { nav(inAdmin ? '/dashboard/admin' : '/dashboard'); onClose(); }} className="flex items-center gap-2.5" data-testid="sidebar-logo">
            <BrandLogo height={28} />
            <div className="text-[10px] text-fab-text-muted uppercase tracking-widest font-semibold" data-testid="sidebar-plan-chip">
              {inAdmin ? 'PLATFORM ADMIN' : (() => {
                // Feb 2026 — during the 15-day PRO Free Trial the sidebar
                // reads "PRO TRIAL" so sellers see the entitlements they
                // actually have. Backend gates still enforce the same rule.
                const now = new Date().toISOString();
                const tier = seller?.plan_tier || 'FREE_TRIAL';
                const trialActive = tier === 'FREE_TRIAL' && seller?.trial_ends_at && now < seller.trial_ends_at;
                return trialActive ? 'PRO TRIAL' : tier;
              })()}
            </div>
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-fab-elevated lg:hidden"
            aria-label="Close menu"
            data-testid="sidebar-close"
          >
            <X className="w-5 h-5 text-fab-text-secondary" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {(inAdmin ? platformAdminNav : items.filter((it) => (!allowedDepartments || allowedDepartments.has(it.label)) && (it.label !== 'Sales Brain' || canSeeSalesBrain))).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={onClose}
              data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors ${
                isActive
                  ? 'bg-fab-primary-lighter text-fab-primary'
                  : 'text-fab-text-secondary hover:bg-fab-elevated hover:text-fab-text'
              }`}
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-fab-primary rounded-r" />}
                  <it.icon className="w-[18px] h-[18px]" />
                  <span className="flex-1">{it.label}</span>
                  {badgeFor(it.badgeKey) !== null && (
                    <span data-testid={`nav-badge-${it.badgeKey}`} className="min-w-[20px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {badgeFor(it.badgeKey)}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
          {inAdmin && (
            <div data-testid="nav-affiliate-group">
              <button
                onClick={() => setAffiliateOpen((o) => !o)}
                data-testid="nav-affiliate-toggle"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium text-fab-text-secondary hover:bg-fab-elevated hover:text-fab-text transition-colors"
              >
                <Handshake className="w-[18px] h-[18px]" />
                <span className="flex-1 text-left">Affiliate Marketers</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${affiliateOpen ? 'rotate-180' : ''}`} />
              </button>
              {affiliateOpen && (
                <div className="ml-3 pl-3 border-l border-fab-primary-lighter space-y-0.5 mt-0.5">
                  {[
                    { to: '/dashboard/admin/affiliate/professional', label: 'Professional' },
                    { to: '/dashboard/admin/affiliate/community', label: 'Community' },
                  ].map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      onClick={onClose}
                      data-testid={`nav-affiliate-${c.label.toLowerCase()}`}
                      className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                        isActive ? 'bg-fab-primary-lighter text-fab-primary' : 'text-fab-text-secondary hover:bg-fab-elevated hover:text-fab-text'
                      }`}
                    >
                      <span>{c.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
          {!inAdmin && seller?.is_admin && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-fab-text-muted pt-4 px-3 pb-1 font-semibold">Admin</div>
              {adminItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={onClose}
                  data-testid={`nav-admin-${it.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors ${
                    isActive
                      ? 'bg-fab-primary-lighter text-fab-primary'
                      : 'text-fab-text-secondary hover:bg-fab-elevated hover:text-fab-text'
                  }`}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-fab-primary rounded-r" />}
                      <it.icon className="w-[18px] h-[18px]" />
                      <span>{it.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="p-3 border-t border-fab-primary-lighter space-y-2">
          {!isTeamMember && (
            <div className="rounded-xl bg-gradient-to-br from-fab-primary-lighter to-white p-3 border border-fab-primary-lighter">
              <div className="text-[11px] uppercase tracking-wider text-fab-primary font-semibold mb-1">Upgrade your plan</div>
              <div className="text-[12px] text-fab-text-secondary mb-2">Unlock unlimited products & video calls.</div>
              <button onClick={() => { nav('/pricing'); onClose(); }} data-testid="upgrade-btn" className="w-full text-[12px] btn-primary py-1.5">View Plans</button>
            </div>
          )}
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 w-full px-3 py-2.5 sm:py-2 text-[13px] sm:text-[12px] text-fab-text-secondary hover:bg-fab-elevated rounded-lg">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}
